import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  CheckCircle, Circle, FileText, GraduationCap, Rocket,
  DownloadSimple, PenNib, ArrowRight, ShieldCheck, SpinnerGap,
  ArrowLeft, Eye, UploadSimple, IdentificationCard
} from "@phosphor-icons/react";

const STEPS = [
  { key: "id_rtw", label: "ID & Right to Work", icon: ShieldCheck, description: "Upload your identification and right to work documents" },
  { key: "agreements", label: "Sign Agreements", icon: FileText, description: "Review and sign your NDA and role agreement" },
  { key: "academy", label: "Complete Academy", icon: GraduationCap, description: "Finish your Banks Morgan training" },
  { key: "hub", label: "Access Hub", icon: Rocket, description: "Full access to the Recruitment Hub" },
];

export default function OnboardingPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [signing, setSigning] = useState(null);
  const [signName, setSignName] = useState("");
  const [loading, setLoading] = useState(true);
  const [readingAgreement, setReadingAgreement] = useState(null);
  const [agreementClauses, setAgreementClauses] = useState([]);
  const [hasScrolledBottom, setHasScrolledBottom] = useState(false);
  const [idRtwDocs, setIdRtwDocs] = useState([]);
  const [idVerifiedExternally, setIdVerifiedExternally] = useState(false);
  const [idRtwPassed, setIdRtwPassed] = useState(false);
  const scrollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, agRes, idRes] = await Promise.all([
        api.get("/onboarding/status"),
        api.get("/agreements"),
        api.get("/onboarding/id-rtw/my-documents"),
      ]);
      setStatus(statusRes.data);
      setAgreements(agRes.data.items || []);
      setIdRtwDocs(idRes.data.documents || []);
      setIdVerifiedExternally(idRes.data.id_verified_externally || false);
      // Previously verified users or users who uploaded both docs can proceed
      const hasPhotoId = (idRes.data.documents || []).some(d => d.doc_type === "photo_id");
      const hasRtw = (idRes.data.documents || []).some(d => d.doc_type === "right_to_work");
      setIdRtwPassed(idRes.data.id_verified_externally || (hasPhotoId && hasRtw));
    } catch (err) {
      console.error("Failed to fetch onboarding status", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const currentStep = !status ? 0
    : status.onboarding_status === "registered" && !idRtwPassed ? 0
    : status.onboarding_status === "registered" ? 1
    : status.onboarding_status === "agreements_signed" ? 2
    : status.onboarding_status === "active" ? 3
    : !idRtwPassed ? 0
    : 1;

  const openAgreementReader = async (agreement) => {
    try {
      const { data } = await api.get(`/agreements/${agreement.id}`);
      setReadingAgreement(data);
      setAgreementClauses(data.clauses || []);
      setHasScrolledBottom(false);
      setSignName(user?.name || "");
    } catch {
      toast.error("Failed to load agreement");
    }
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) setHasScrolledBottom(true);
  };

  async function handleSign(agreementId) {
    if (!signName.trim()) { toast.error("Please type your full name to sign"); return; }
    try {
      const { data } = await api.post(`/agreements/${agreementId}/sign`, {
        signature_name: signName,
        agreed_terms: true,
      });
      toast.success(data.message);
      setReadingAgreement(null);
      setSigning(null);
      setSignName("");
      setHasScrolledBottom(false);
      await fetchStatus();
      if (data.all_signed) {
        await checkAuth();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign");
    }
  }

  async function handleDownloadPdf(agreementId, title) {
    try {
      const response = await api.get(`/agreements/${agreementId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/\s+/g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <SpinnerGap size={32} className="text-[#C5A059] animate-spin" />
    </div>
  );

  if (status?.onboarding_status === "active") {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <Rocket size={48} className="text-[#C5A059] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0F172A] mb-2">You&apos;re All Set!</h2>
          <p className="text-[#6B7280] mb-6">Your onboarding is complete. Welcome to Banks Morgan.</p>
          <Button
            onClick={() => navigate("/hub")}            className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-bold"
            data-testid="go-to-hub-btn"
          >
            Go to Recruitment Hub
          </Button>
        </div>
      </div>
    );
  }

  // ===== Embedded Agreement Reader =====
  if (readingAgreement && readingAgreement.status !== "signed") {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <div className="bg-[#0F172A] border-b border-[#1E293B]">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setReadingAgreement(null); setHasScrolledBottom(false); }}
                className="text-[#94A3B8] hover:text-white transition flex items-center gap-1"
                data-testid="back-to-agreements-btn"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-[#C5A059] text-[#0F172A] font-extrabold text-sm px-2.5 py-1 rounded-md">BM</div>
              <span className="text-white font-bold text-sm">Banks Morgan</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
            {/* Agreement Header */}
            <div className="bg-[#0F172A] px-6 py-5 text-center">
              <h2 className="text-lg font-bold text-[#C5A059]">{readingAgreement.title}</h2>
              <p className="text-xs text-[#94A3B8] mt-1">
                {readingAgreement.entity_name} &mdash; Please read all clauses below
              </p>
            </div>

            {/* Scrollable Agreement Body */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="px-6 py-4 overflow-y-auto"
              style={{ maxHeight: "420px" }}
              data-testid="agreement-scroll-container"
            >
              <div className="mb-4 pb-3 border-b border-[#E5E7EB]">
                <p className="text-sm text-[#374151]">
                  <strong>Date:</strong> {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <p className="text-sm text-[#374151]">
                  <strong>Party 1 (Company):</strong> {readingAgreement.entity_name}
                </p>
                <p className="text-sm text-[#374151]">
                  <strong>Party 2 (Individual):</strong> {readingAgreement.user_name}
                </p>
              </div>

              {agreementClauses.map((clause, i) => (
                <div key={i} className="mb-4">
                  <h4 className="text-sm font-bold text-[#0F172A] mb-1">{clause.heading}</h4>
                  <p className="text-sm text-[#374151] leading-relaxed">{clause.body}</p>
                </div>
              ))}

              <div className="mt-6 pt-4 border-t border-[#E5E7EB] text-center">
                <p className="text-xs text-[#9CA3AF]">
                  Banks Morgan Recruitment | A Trading Name of Hitem Hard Media Ltd
                </p>
                <p className="text-xs text-[#9CA3AF]">
                  End of Agreement &mdash; {readingAgreement.title}
                </p>
              </div>
            </div>

            {/* Scroll indicator */}
            {!hasScrolledBottom && (
              <div className="px-6 py-2 bg-[#FFF7ED] border-t border-[#FED7AA] text-center" data-testid="scroll-notice">
                <p className="text-xs text-[#D97706] font-medium">
                  Please scroll to the bottom of the agreement to enable signing
                </p>
              </div>
            )}

            {/* Signature Section */}
            <div className="px-6 py-5 bg-[#F9FAFB] border-t border-[#E5E7EB]" data-testid="embedded-sign-section">
              <p className="text-sm text-[#374151] mb-3">
                By typing your full legal name below, you confirm you have read and agree to the terms of this <strong>{readingAgreement.title}</strong>.
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={signName}
                  onChange={(e) => setSignName(e.target.value)}
                  placeholder="Type your full legal name"
                  className="flex-1 border border-[#D1D5DB] rounded-lg px-3 py-2.5 text-sm focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                  data-testid="embedded-sign-name-input"
                  disabled={!hasScrolledBottom}
                />
                <Button
                  className="bg-[#0F172A] text-white hover:bg-[#1E293B] font-semibold gap-1.5"
                  onClick={() => handleSign(readingAgreement.id)}
                  disabled={!hasScrolledBottom || !signName.trim()}
                  data-testid="embedded-confirm-sign-btn"
                >
                  <PenNib size={14} /> Sign Agreement
                </Button>
              </div>
              {!hasScrolledBottom && (
                <p className="text-xs text-[#9CA3AF] mt-2">You must scroll to the bottom of the agreement before signing.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Top Bar */}
      <div className="bg-[#0F172A] border-b border-[#1E293B]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#C5A059] text-[#0F172A] font-extrabold text-sm px-2.5 py-1 rounded-md">BM</div>
            <span className="text-white font-bold">Banks Morgan</span>
          </div>
          <div className="text-[#94A3B8] text-sm">
            <span className="text-white font-medium">{user?.name}</span>
            <span className="ml-2 text-xs uppercase px-2 py-0.5 bg-[#1E293B] rounded text-[#C5A059] font-semibold">Onboarding</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10" data-testid="onboarding-steps">
          {STEPS.map((step, idx) => (
            <div key={step.key} className="flex items-center">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                idx < currentStep
                  ? "bg-green-100 text-green-800"
                  : idx === currentStep
                  ? "bg-[#0F172A] text-[#C5A059]"
                  : "bg-[#F1F5F9] text-[#94A3B8]"
              }`}>
                {idx < currentStep ? (
                  <CheckCircle size={18} weight="fill" />
                ) : (
                  <step.icon size={18} />
                )}
                <span className="hidden sm:inline">{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${idx < currentStep ? "bg-green-300" : "bg-[#E2E8F0]"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 0: ID & Right to Work */}
        {currentStep === 0 && (
          <IdRtwStep
            idVerifiedExternally={idVerifiedExternally}
            idRtwDocs={idRtwDocs}
            onComplete={() => fetchStatus()}
          />
        )}

        {/* Step 1: Agreements */}
        {currentStep === 1 && (
          <div data-testid="onboarding-agreements-section">
            <div className="text-center mb-8">
              <ShieldCheck size={40} className="text-[#C5A059] mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-[#0F172A]">Review & Sign Your Agreements</h2>
              <p className="text-[#6B7280] mt-2 max-w-md mx-auto">
                Please read each agreement in full and sign electronically to proceed to training.
              </p>
            </div>

            <div className="space-y-4">
              {agreements.map((ag) => (
                <div
                  key={ag.id}
                  className={`bg-white rounded-xl border p-6 transition-all ${
                    ag.status === "signed"
                      ? "border-green-200 bg-green-50/30"
                      : "border-[#E5E7EB] hover:border-[#C5A059]"
                  }`}
                  data-testid={`agreement-card-${ag.agreement_type}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {ag.status === "signed" ? (
                        <CheckCircle size={24} className="text-green-600 mt-0.5" weight="fill" />
                      ) : (
                        <Circle size={24} className="text-[#D1D5DB] mt-0.5" />
                      )}
                      <div>
                        <h3 className="font-bold text-[#0F172A]">{ag.title}</h3>
                        <p className="text-sm text-[#6B7280] mt-0.5">
                          {ag.status === "signed"
                            ? `Signed by ${ag.signature_name} on ${new Date(ag.signed_at).toLocaleDateString("en-GB")}`
                            : `${ag.entity_name} — Awaiting your signature`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => handleDownloadPdf(ag.id, ag.title)}
                        data-testid={`download-${ag.agreement_type}-btn`}
                      >
                        <DownloadSimple size={14} /> PDF
                      </Button>
                      {ag.status === "pending" && (
                        <Button
                          size="sm"
                          className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] gap-1.5 font-semibold"
                          onClick={() => openAgreementReader(ag)}
                          data-testid={`read-sign-${ag.agreement_type}-btn`}
                        >
                          <Eye size={14} /> Read & Sign
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Academy */}
        {currentStep === 2 && (
          <div className="text-center" data-testid="onboarding-academy-section">
            <GraduationCap size={48} className="text-[#C5A059] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Complete Your Training</h2>
            <p className="text-[#6B7280] mb-6 max-w-md mx-auto">
              You&apos;ve signed all your agreements. Now complete the Banks Morgan Academy to unlock your Recruitment Hub.
            </p>
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 max-w-md mx-auto mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-[#374151]">Academy Progress</span>
                <span className="text-sm font-bold text-[#C5A059]">
                  {status?.academy?.completed_modules || 0}/{status?.academy?.total_modules || 0} modules
                </span>
              </div>
              <div className="w-full bg-[#F1F5F9] rounded-full h-2.5">
                <div
                  className="bg-[#C5A059] rounded-full h-2.5 transition-all"
                  style={{ width: `${status?.academy?.total_modules ? ((status.academy.completed_modules / status.academy.total_modules) * 100) : 0}%` }}
                />
              </div>
            </div>
            <Button
              onClick={() => navigate("/hub/academy")}
              className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-bold gap-2"
              data-testid="go-to-academy-btn"
            >
              <GraduationCap size={18} /> Go to Academy <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


function IdRtwStep({ idVerifiedExternally, idRtwDocs, onComplete }) {
  const [uploading, setUploading] = useState(false);
  const hasPhotoId = idRtwDocs.some(d => d.doc_type === "photo_id");
  const hasRtw = idRtwDocs.some(d => d.doc_type === "right_to_work");

  const handleFileUpload = async (docType, file) => {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        await api.post("/onboarding/id-rtw/upload", {
          doc_type: docType,
          filename: file.name,
          file_data: base64,
        });
        toast.success(`${docType === "photo_id" ? "Photo ID" : "Right to Work"} uploaded`);
        onComplete();
      };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const triggerUpload = (docType) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) handleFileUpload(docType, file);
    };
    input.click();
  };

  return (
    <div data-testid="onboarding-id-rtw-section">
      <div className="text-center mb-8">
        <IdentificationCard size={40} className="text-[#C5A059] mx-auto mb-3" />
        <h2 className="text-2xl font-bold text-[#0F172A]">ID & Right to Work</h2>
        <p className="text-[#6B7280] mt-2 max-w-md mx-auto">
          Upload your identification and right to work documentation for our records.
        </p>
      </div>

      {idVerifiedExternally && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 max-w-lg mx-auto flex items-center gap-3" data-testid="previously-verified-badge">
          <ShieldCheck size={24} className="text-green-600" weight="fill" />
          <div>
            <p className="text-sm font-semibold text-green-800">Previously Verified by Banks Morgan</p>
            <p className="text-xs text-green-600">Your identity and right to work have been verified. You may upload documents below for record keeping.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg mx-auto">
        {/* Photo ID */}
        <div className={`bg-white rounded-xl border p-5 text-center ${hasPhotoId ? "border-green-200 bg-green-50/30" : "border-[#E5E7EB]"}`}>
          <IdentificationCard size={32} className={hasPhotoId ? "text-green-600 mx-auto mb-2" : "text-[#C5A059] mx-auto mb-2"} weight="duotone" />
          <h3 className="text-sm font-bold text-[#0F172A] mb-1">Photographic ID</h3>
          <p className="text-xs text-[#6B7280] mb-3">Passport, driving licence, or national ID</p>
          {hasPhotoId ? (
            <div className="flex items-center justify-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle size={16} weight="fill" /> Uploaded
            </div>
          ) : (
            <Button size="sm" onClick={() => triggerUpload("photo_id")} disabled={uploading} className="bg-[#0F172A] gap-1" data-testid="upload-photo-id-btn">
              <UploadSimple size={14} /> Upload
            </Button>
          )}
        </div>

        {/* Right to Work */}
        <div className={`bg-white rounded-xl border p-5 text-center ${hasRtw ? "border-green-200 bg-green-50/30" : "border-[#E5E7EB]"}`}>
          <FileText size={32} className={hasRtw ? "text-green-600 mx-auto mb-2" : "text-[#C5A059] mx-auto mb-2"} weight="duotone" />
          <h3 className="text-sm font-bold text-[#0F172A] mb-1">Right to Work</h3>
          <p className="text-xs text-[#6B7280] mb-3">Right to work documentation</p>
          {hasRtw ? (
            <div className="flex items-center justify-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle size={16} weight="fill" /> Uploaded
            </div>
          ) : (
            <Button size="sm" onClick={() => triggerUpload("right_to_work")} disabled={uploading} className="bg-[#0F172A] gap-1" data-testid="upload-rtw-btn">
              <UploadSimple size={14} /> Upload
            </Button>
          )}
        </div>
      </div>

      {/* Continue button for previously verified users */}
      {idVerifiedExternally && (
        <div className="text-center mt-6">
          <Button onClick={onComplete} className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-bold gap-2" data-testid="continue-after-id-rtw-btn">
            Continue to Agreements <ArrowRight size={16} />
          </Button>
        </div>
      )}

      {!idVerifiedExternally && hasPhotoId && hasRtw && (
        <div className="text-center mt-6">
          <p className="text-sm text-[#6B7280] mb-3">Both documents uploaded. Click continue to proceed.</p>
          <Button onClick={onComplete} className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-bold gap-2" data-testid="continue-after-id-rtw-btn">
            Continue to Agreements <ArrowRight size={16} />
          </Button>
        </div>
      )}
    </div>
  );
}
